import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { User, Building2, Mail, Tag as TagIcon, StickyNote, Save, Phone, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useTags } from "@/context/AuthContext";
import { DeleteDataButton } from "@/components/inbox/DeleteData";

const Field = ({ icon: Icon, label, children }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Icon size={12} />{label}</label>
    {children}
  </div>
);

export const CrmPanel = ({ conversation: c, className = "flex" }) => {
  const qc = useQueryClient();
  const { data: tags = [] } = useTags();
  const [form, setForm] = useState({ name: c.name, company: c.company, email: c.email, tags: c.tags, crm_notes: c.crm_notes });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleTag = (t) => setForm({ ...form, tags: form.tags.includes(t) ? form.tags.filter((x) => x !== t) : [...form.tags, t] });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/conversations/${c.id}`, form);
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["conversation", c.id] });
      toast.success("Dati cliente salvati");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside className={`w-80 shrink-0 border-l border-slate-200 bg-white flex-col ${className}`} data-testid="crm-panel">
      <div className="h-16 px-5 flex items-center border-b border-slate-200">
        <h3 className="font-bold text-slate-900">Scheda cliente</h3>
        <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#DCFCE7] text-[#15803D]">CRM</span>
      </div>
      <div className="flex-1 overflow-y-auto thin-scroll p-5 space-y-4">
        <Field icon={Phone} label="Numero WhatsApp"><div className="font-mono text-sm text-slate-700 bg-slate-50 rounded-md px-3 py-2 border border-slate-200">{c.phone}</div></Field>
        <Field icon={User} label="Nome completo"><Input data-testid="crm-name-input" value={form.name} onChange={set("name")} /></Field>
        <Field icon={Building2} label="Azienda"><Input data-testid="crm-company-input" value={form.company} onChange={set("company")} /></Field>
        <Field icon={Mail} label="Email"><Input data-testid="crm-email-input" type="email" value={form.email} onChange={set("email")} /></Field>
        <Field icon={TagIcon} label="Tag">
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t) => {
              const on = form.tags.includes(t.name);
              return (
                <button key={t.id} data-testid={`crm-tag-${t.name.toLowerCase()}`} onClick={() => toggleTag(t.name)}
                  className={`text-xs font-medium px-2.5 py-1 rounded-full border flex items-center gap-1.5 transition-colors ${on ? "text-white border-transparent" : "text-slate-600 border-slate-200 hover:border-slate-300"}`}
                  style={on ? { background: t.color } : undefined}>
                  {!on && <i className="w-2 h-2 rounded-full" style={{ background: t.color }} />}
                  {t.name}
                  {on && <X size={11} />}
                </button>
              );
            })}
          </div>
        </Field>
        <Field icon={StickyNote} label="Note permanenti account">
          <Textarea data-testid="crm-notes-textarea" rows={6} value={form.crm_notes} onChange={set("crm_notes")} placeholder="Preferenze, condizioni commerciali, storico…" />
        </Field>
      </div>
      <div className="p-4 border-t border-slate-200">
        <Button data-testid="crm-save-notes-button" onClick={save} disabled={saving} className="w-full bg-[#0F172A] hover:bg-[#1E293B]">
          <Save size={15} className="mr-2" />{saving ? "Salvataggio…" : "Salva scheda cliente"}
        </Button>
        <DeleteDataButton conversation={c} className="w-full justify-center mt-2 bg-white !text-red-600 border border-red-200 hover:!bg-red-50" />
      </div>
    </aside>
  );
};
